import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

export const GEODATA_TYPE_OPTIONS = ["CITY", "ADM2", "ISLAND"] as const;
// "Sports" since the sports_match content type (2026-10). Zod rejects an
// unknown section, and the event create page parses the venue's geodata
// when a venue is picked — so a city tagged "Sports" blocked Save & Continue.
export const GEODATA_SECTION_OPTIONS = [
  "Music",
  "Cinema",
  "Theater",
  "Sports",
] as const;

/**
 * SUBMIT schema for a geodata enrichment.
 *
 * `.passthrough()` for the same reason as the other four: the enrich endpoint
 * full-replaces, so a key Zod strips is destroyed. `taskName` happens to be
 * modelled here, but the next pipeline-owned key will not be.
 */
export const geodataEnrichmentSubmitSchema = z
  .object({
    about: z.string().default(""),
    aliases: z.array(z.string()).default([]),
    updatedAt: z.string().datetime({ offset: true }).nullish(),
    taskName: z.string().nullish(),
  })
  .passthrough();

/** READ schema — lenient. Consume through `parseRead()`. */
export const geodataEnrichmentReadSchema = z
  .object({
    about: z.string().nullish(),
    aliases: z.array(z.string()).nullish(),
    updatedAt: z.string().nullish(),
    taskName: z.string().nullish(),
  })
  .passthrough();

export type GeodataEnrichment = z.infer<typeof geodataEnrichmentSubmitSchema>;
export type GeodataEnrichmentRead = z.infer<typeof geodataEnrichmentReadSchema>;
/** @deprecated Prefer the explicit read/submit pair. Kept so existing importers compile. */
export { geodataEnrichmentSubmitSchema as geodataEnrichmentSchema };

export const geodataEditSchema = z.object({
  id: z.coerce.string().superRefine(
    fieldConfig({
      inputProps: {
        disabled: true,
      },
    }),
  ),
  name: z.string(),
  country: z.string(),
  countryCode: z.string(),
  region: z.string(),
  type: z.enum(GEODATA_TYPE_OPTIONS),
  timezone: z.string().nullish(),
  population: z.coerce.number().nullish(),
  regionWdId: z.string().nullish(),
  regionCode: z.string().nullish(),
  wikiDataId: z.string().nullish(),
  geoDbId: z.coerce.string().nullish(),
  moeMapping: z
    .array(z.string())
    .nullish()
    .superRefine(fieldConfig({ fieldType: "moeMapping" })),
  hide: z.boolean().nullish().default(false),
  geolocation: z
    .object({
      latitude: z.coerce.number(),
      longitude: z.coerce.number(),
    })
    .nullish(),
  sections: z.array(z.enum(GEODATA_SECTION_OPTIONS)).nullish(),
});

// GeoDB places (e.g. islands) may come back without a region
export const geodataGeoDbResultSchema = geodataEditSchema.extend({
  region: z.string().optional(),
});

export type GeodataGeoDbResultType = z.infer<typeof geodataGeoDbResultSchema>;

/**
 * READ schema for the geodata edit page.
 *
 * Lenient + `.passthrough()`, consumed through `parseRead()`. It used to nest
 * the strict enrichment schema behind a hard `.parse()` in
 * `use-geodata-repository`, so a stored enrichment with an unexpected key
 * blanked the page.
 */
export const geodataViewSchema = geodataEditSchema
  .extend({
    enrichmentStatus: z.string().nullish(),
    embeddingStatus: z.string().nullish(),
    enrichmentRetryCount: z.coerce.number().nullish(),
    enrichment: geodataEnrichmentReadSchema.nullish(),
  })
  .passthrough();

export type GeodataViewType = z.infer<typeof geodataViewSchema>;

export type GeodataEditFormType = z.infer<typeof geodataEditSchema>;
